import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AppShell,
  Button,
  Chip,
  Dialog,
  EmptyState,
  Icon,
  Input,
  PeriodPicker,
  SegmentedControl,
  SelectMenu,
  Sheet,
  Skeleton,
  StatCard
} from '../../ui';
import NovaTransacaoSheet from './NovaTransacaoSheet';
import { useTransacoes } from '../../servicos/transacoes/useTransacoes';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { useFiltroContexto } from '../../contextos/FiltroContexto';
import { useCategoriaContexto } from '../../contextos/CategoriaContexto';
import { useContaContexto } from '../../contextos/ContaContexto';
import { useCartaoCreditoContexto } from '../../contextos/CartaoCreditoContexto';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
import { ehPagamentoFatura, ehSaldoInicial, ehTransferenciaEntreContas, TRANSFERENCIA_CONTAS, valorParaTexto } from '../../utils/Utils';
import { agruparPorDia, chaveDia, filtrarTransacoes, totaisDaLista } from './lista';
import styles from './Movimentacoes.module.css';

type CampoLote = 'categoria' | 'conta' | 'pagamento';

// IDs EXATOS armazenados pelo backend (mesma lista do formulário antigo).
const TIPOS_PAGAMENTO = [
  { id: 'PIX', icone: 'qr_code_2' },
  { id: 'Dinheiro', icone: 'payments' },
  { id: 'Cartão de Crédito', icone: 'credit_card' },
  { id: 'Cartão de Debito', icone: 'credit_score' },
  { id: 'Boleto', icone: 'receipt_long' },
  { id: 'Transferência', icone: 'sync_alt' },
  { id: 'VR', icone: 'restaurant' },
  { id: 'VA', icone: 'shopping_cart' }
];

const TITULOS_LOTE: Record<CampoLote, string> = {
  categoria: 'Alterar categoria',
  conta: 'Alterar conta',
  pagamento: 'Alterar forma de pagamento'
};

const formatarDia = (data: Date): string => {
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);

  let prefixo = data.toLocaleDateString('pt-BR', { weekday: 'long' }).replace('-feira', '');
  if (chaveDia(data) === chaveDia(hoje)) prefixo = 'hoje';
  else if (chaveDia(data) === chaveDia(ontem)) prefixo = 'ontem';

  const resto = data.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
  const ano = data.getFullYear() !== hoje.getFullYear() ? ` de ${data.getFullYear()}` : '';
  return `${prefixo}, ${resto}${ano}`;
};

export default function Movimentacoes() {
  const { transacoes, loading, fetchTransacoes, deleteTransacao, bulkUpdateTransacao } = useTransacoes();
  const { categorias } = useCategoriaContexto();
  const { contas } = useContaContexto();
  const { cartoes } = useCartaoCreditoContexto();
  const { filtros, setFiltros } = useFiltroContexto();
  const { atualizar } = useAtualizacaoContexto();

  const [busca, setBusca] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todas');
  // Filtros múltiplos: dá pra marcar 2 cartões, 3 contas etc. de uma vez.
  const [categoriasFiltro, setCategoriasFiltro] = useState<string[]>([]);
  const [contasFiltro, setContasFiltro] = useState<string[]>([]);
  const [cartoesFiltro, setCartoesFiltro] = useState<string[]>([]);
  const [pagamentosFiltro, setPagamentosFiltro] = useState<string[]>([]);

  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [idsExcluir, setIdsExcluir] = useState<string[]>([]);
  const [campoLote, setCampoLote] = useState<CampoLote | null>(null);
  const [valorLote, setValorLote] = useState('');

  const [sheetAberta, setSheetAberta] = useState(false);
  const [transacaoEdicao, setTransacaoEdicao] = useState<Transacao | null>(null);
  const [tipoInicial, setTipoInicial] = useState<'D' | 'R'>('D');

  const [buscou, setBuscou] = useState(false);

  // Drill-down vindo de outras telas (dashboard/contas/cartões): chega via
  // location.state com filtros e período prontos — aplica uma vez e limpa,
  // pra F5 não re-aplicar um filtro que o usuário já removeu.
  const location = useLocation();
  useEffect(() => {
    const estado = location.state as {
      categoriaIds?: string[];
      contaIds?: string[];
      cartaoIds?: string[];
      dataInicial?: string; // AAAA-MM-DD
      dataFinal?: string;
    } | null;
    if (!estado) return;
    if (estado.categoriaIds?.length) setCategoriasFiltro(estado.categoriaIds);
    if (estado.contaIds?.length) setContasFiltro(estado.contaIds);
    if (estado.cartaoIds?.length) setCartoesFiltro(estado.cartaoIds);
    if (estado.dataInicial && estado.dataFinal) {
      const [ai, mi, di] = estado.dataInicial.split('-').map(Number);
      const [af, mf, df] = estado.dataFinal.split('-').map(Number);
      setFiltros((atuais) => ({
        ...atuais,
        movimentacao: { dataInicial: new Date(ai, mi - 1, di), dataFinal: new Date(af, mf - 1, df) }
      }));
    }
    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const buscar = async () => {
      await fetchTransacoes({
        dataInicial: filtros.movimentacao.dataInicial,
        dataFinal: filtros.movimentacao.dataFinal
      });
      setBuscou(true);
    };
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.movimentacao, atualizar]);

  const filtradas = filtrarTransacoes(transacoes, {
    busca,
    tipo: tipoFiltro,
    categoriaIds: categoriasFiltro,
    contaIds: contasFiltro,
    cartaoIds: cartoesFiltro,
    pagamentos: pagamentosFiltro
  });

  // Pagamento de fatura não soma como despesa (as compras do cartão já contam):
  // aparece na lista com selo, mas fica fora dos totais.
  const { receitas: totalReceitas, despesas: totalDespesas, saldo } = totaisDaLista(filtradas);

  // Paginação incremental: renderiza em lotes para a tela aguentar períodos
  // grandes (milhares de itens) sem travar o DOM. Totais usam o conjunto todo.
  const LOTE = 150;
  const [limite, setLimite] = useState(LOTE);

  useEffect(() => {
    setLimite(LOTE);
  }, [filtros.movimentacao, atualizar, busca, tipoFiltro, categoriasFiltro, contasFiltro, cartoesFiltro, pagamentosFiltro]);

  const grupos = useMemo(
    () => agruparPorDia(filtradas, limite),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transacoes, busca, tipoFiltro, categoriasFiltro, contasFiltro, cartoesFiltro, pagamentosFiltro, limite]
  );

  const restantes = filtradas.length - limite;

  const opcoesCategoria = categorias.map((c) => ({
    id: c.id?.toString() ?? '',
    rotulo: c.nome,
    icone: c.icone,
    cor: c.corIdentificacao
  }));
  const opcoesConta = contas.map((c) => ({
    id: c.id?.toString() ?? '',
    rotulo: c.descricao,
    icone: 'account_balance'
  }));
  const opcoesCartao = cartoes.map((c) => ({
    id: c.id?.toString() ?? '',
    rotulo: c.nome,
    icone: 'credit_card'
  }));
  const opcoesPagamento = [
    ...TIPOS_PAGAMENTO.map((p) => ({ id: p.id, rotulo: p.id, icone: p.icone })),
    { id: TRANSFERENCIA_CONTAS, rotulo: TRANSFERENCIA_CONTAS, icone: 'swap_horiz' }
  ];

  const limparFiltros = () => {
    setBusca('');
    setTipoFiltro('todas');
    setCategoriasFiltro([]);
    setContasFiltro([]);
    setCartoesFiltro([]);
    setPagamentosFiltro([]);
  };

  const removerDe = (setter: React.Dispatch<React.SetStateAction<string[]>>, id: string) => () =>
    setter((atuais) => atuais.filter((atual) => atual !== id));

  // Um chip removível POR item selecionado (filtros múltiplos geram vários chips).
  const chipsAtivos: { chave: string; rotulo: string; icone?: string; cor?: string; remover: () => void }[] = [];
  if (busca) chipsAtivos.push({ chave: 'busca', rotulo: `"${busca}"`, icone: 'search', remover: () => setBusca('') });
  if (tipoFiltro !== 'todas') {
    chipsAtivos.push({
      chave: 'tipo',
      rotulo: tipoFiltro === 'D' ? 'Despesas' : 'Receitas',
      icone: tipoFiltro === 'D' ? 'trending_down' : 'trending_up',
      remover: () => setTipoFiltro('todas')
    });
  }
  categoriasFiltro.forEach((id) => {
    const cat = categorias.find((c) => c.id?.toString() === id);
    chipsAtivos.push({
      chave: `categoria-${id}`,
      rotulo: cat?.nome ?? 'Categoria',
      icone: cat?.icone ?? 'category',
      cor: cat?.corIdentificacao,
      remover: removerDe(setCategoriasFiltro, id)
    });
  });
  contasFiltro.forEach((id) =>
    chipsAtivos.push({
      chave: `conta-${id}`,
      rotulo: contas.find((c) => c.id?.toString() === id)?.descricao ?? 'Conta',
      icone: 'account_balance',
      remover: removerDe(setContasFiltro, id)
    })
  );
  cartoesFiltro.forEach((id) =>
    chipsAtivos.push({
      chave: `cartao-${id}`,
      rotulo: cartoes.find((c) => c.id?.toString() === id)?.nome ?? 'Cartão',
      icone: 'credit_card',
      remover: removerDe(setCartoesFiltro, id)
    })
  );
  pagamentosFiltro.forEach((id) =>
    chipsAtivos.push({
      chave: `pagamento-${id}`,
      rotulo: id,
      icone: 'payments',
      remover: removerDe(setPagamentosFiltro, id)
    })
  );

  const novaTransacao = (tipo: 'D' | 'R') => {
    setTransacaoEdicao(null);
    setTipoInicial(tipo);
    setSheetAberta(true);
  };

  const abrirEdicao = (t: Transacao) => {
    setTransacaoEdicao(t);
    setSheetAberta(true);
  };

  const alternarSelecao = (id: string) => {
    setSelecionados((atuais) => (atuais.includes(id) ? atuais.filter((s) => s !== id) : [...atuais, id]));
  };

  const confirmarExclusao = async () => {
    const ids = idsExcluir;
    await deleteTransacao(ids);
    setIdsExcluir([]);
    setSelecionados((atuais) => atuais.filter((id) => !ids.includes(id)));
  };

  const fecharLote = () => {
    setCampoLote(null);
    setValorLote('');
  };

  const aplicarLote = async () => {
    if (!campoLote || !valorLote) return;
    if (campoLote === 'categoria') await bulkUpdateTransacao(selecionados, valorLote, null, null, null);
    else if (campoLote === 'conta') await bulkUpdateTransacao(selecionados, null, valorLote, null, null);
    else await bulkUpdateTransacao(selecionados, null, null, null, valorLote);
    fecharLote();
    setSelecionados([]);
  };

  const opcoesLote =
    campoLote === 'categoria' ? opcoesCategoria : campoLote === 'conta' ? opcoesConta : opcoesPagamento;

  const carregandoLista = (!buscou || loading) && transacoes.length === 0;
  const semTransacoes = buscou && !loading && transacoes.length === 0;
  const semResultados = buscou && !loading && transacoes.length > 0 && filtradas.length === 0;

  return (
    <AppShell
      titulo="Movimentações"
      subtitulo="Tudo o que entrou e saiu, dia a dia."
      acoes={
        <>
          <PeriodPicker
            inicio={filtros.movimentacao.dataInicial}
            fim={filtros.movimentacao.dataFinal}
            onChange={(inicio, fim) =>
              setFiltros({ ...filtros, movimentacao: { dataInicial: inicio, dataFinal: fim } })
            }
          />
          <Button variante="primaria" icone="add" onClick={() => novaTransacao('D')}>
            Nova transação
          </Button>
        </>
      }
    >
      <div className={styles.toolbar}>
        <div className={styles.busca}>
          <Input valor={busca} onChange={setBusca} icone="search" placeholder="Buscar por descrição..." />
        </div>
        <SegmentedControl
          opcoes={[
            { id: 'todas', rotulo: 'Todas' },
            { id: 'D', rotulo: 'Despesas', icone: 'trending_down' },
            { id: 'R', rotulo: 'Receitas', icone: 'trending_up' }
          ]}
          valor={tipoFiltro}
          onChange={setTipoFiltro}
        />
        <div className={styles.filtroSelect}>
          <SelectMenu
            valores={categoriasFiltro}
            onMudarValores={setCategoriasFiltro}
            opcoes={opcoesCategoria}
            placeholder="Categoria"
            busca
          />
        </div>
        <div className={styles.filtroSelect}>
          <SelectMenu
            valores={contasFiltro}
            onMudarValores={setContasFiltro}
            opcoes={opcoesConta}
            placeholder="Conta"
            busca
          />
        </div>
        <div className={styles.filtroSelect}>
          <SelectMenu
            valores={cartoesFiltro}
            onMudarValores={setCartoesFiltro}
            opcoes={opcoesCartao}
            placeholder="Cartão"
            busca
          />
        </div>
        <div className={styles.filtroSelect}>
          <SelectMenu
            valores={pagamentosFiltro}
            onMudarValores={setPagamentosFiltro}
            opcoes={opcoesPagamento}
            placeholder="Pagamento"
            busca
          />
        </div>
      </div>

      {chipsAtivos.length > 0 && (
        <div className={styles.chipsAtivos}>
          {chipsAtivos.map((chip) => (
            <Chip key={chip.chave} rotulo={chip.rotulo} icone={chip.icone} cor={chip.cor} onRemover={chip.remover} />
          ))}
          <Chip rotulo="Limpar tudo" icone="close" onClick={limparFiltros} />
        </div>
      )}

      {transacoes.length > 0 && (
        <div className={styles.resumo}>
          <StatCard rotulo="Receitas" valor={`+${valorParaTexto(totalReceitas, true)}`} icone="trending_up" tom="positivo" />
          <StatCard rotulo="Despesas" valor={valorParaTexto(totalDespesas, true)} icone="trending_down" tom="neutro" />
          <StatCard
            rotulo="Saldo do período"
            valor={valorParaTexto(saldo, true)}
            icone="account_balance_wallet"
            tom={saldo < 0 ? 'negativo' : 'primario'}
            dica={`${filtradas.length} movimentaç${filtradas.length === 1 ? 'ão' : 'ões'} no filtro`}
          />
        </div>
      )}

      {carregandoLista && <Skeleton tipo="linhas" quantidade={7} altura={56} />}

      {semTransacoes && (
        <EmptyState
          icone="receipt_long"
          titulo="Nenhuma movimentação neste período"
          descricao="Registre sua primeira despesa ou receita. O Finant organiza os lançamentos por dia e por categoria."
          acao={
            <Button variante="primaria" icone="add" onClick={() => novaTransacao('D')}>
              Lançar primeira transação
            </Button>
          }
        />
      )}

      {semResultados && (
        <EmptyState
          icone="filter_alt_off"
          titulo="Nenhum resultado com esses filtros"
          descricao="Ajuste a busca ou remova filtros para voltar a ver suas movimentações."
          acao={
            <Button variante="suave" icone="close" onClick={limparFiltros}>
              Limpar filtros
            </Button>
          }
        />
      )}

      <div className={`${styles.lista} ${selecionados.length > 0 ? styles.selecionando : ''}`}>
        {grupos.map((grupo) => {
          const totalDia = grupo.totalDia;

          return (
            <section key={grupo.chave} className={styles.grupo}>
              <div className={styles.diaCabecalho}>
                <span className={styles.diaNome}>{formatarDia(grupo.data)}</span>
                <span className={styles.diaTotal}>
                  {totalDia > 0 ? '+' : ''}
                  {valorParaTexto(totalDia, true)}
                </span>
              </div>
              {grupo.itens.map((t) => {
                const idTexto = t.id?.toString() ?? '';
                const marcado = selecionados.includes(idTexto);
                const cat = categorias.find((c) => c.id === t.categoriaId);
                const nomeContaOuCartao =
                  cartoes.find((c) => c.id === t.cartaoCreditoId)?.nome ??
                  contas.find((c) => c.id === t.contaId)?.descricao;
                const secundaria = [cat?.nome, t.tipoPagamento, nomeContaOuCartao].filter(Boolean).join(' · ');

                return (
                  <div key={idTexto} className={styles.linha} onClick={() => abrirEdicao(t)}>
                    <input
                      type="checkbox"
                      className={`${styles.check} ${marcado ? styles.checkAtivo : ''}`}
                      checked={marcado}
                      onChange={() => alternarSelecao(idTexto)}
                      onClick={(evento) => evento.stopPropagation()}
                      aria-label="Selecionar movimentação"
                    />
                    <span
                      className={styles.iconeCategoria}
                      style={
                        cat
                          ? { background: `${cat.corIdentificacao}1a`, color: cat.corIdentificacao }
                          : undefined
                      }
                    >
                      <Icon nome={cat?.icone || 'category'} tamanho={18} />
                    </span>
                    <div className={styles.conteudo}>
                      <div className={styles.linhaTitulo}>
                        <span className={styles.descricao}>{t.descricao}</span>
                        {t.ehFixa && (
                          <span className={styles.badge}>
                            <Icon nome="repeat" tamanho={12} />
                            fixa
                          </span>
                        )}
                        {t.ehParcelada && (
                          <span className={styles.badge}>
                            {t.parcela}/{t.totalParcelas}
                          </span>
                        )}
                        {ehPagamentoFatura(t) && (
                          <span className={styles.badge}>
                            <Icon nome="price_check" tamanho={12} />
                            pagamento de fatura
                          </span>
                        )}
                        {ehTransferenciaEntreContas(t) && (
                          <span className={styles.badge}>
                            <Icon nome="swap_horiz" tamanho={12} />
                            transferência
                          </span>
                        )}
                        {ehSaldoInicial(t) && (
                          <span className={styles.badge}>
                            <Icon nome="flag" tamanho={12} />
                            saldo inicial
                          </span>
                        )}
                      </div>
                      {secundaria && <span className={styles.secundaria}>{secundaria}</span>}
                    </div>
                    <div className={styles.acoesLinha}>
                      <button
                        type="button"
                        className={styles.acaoBotao}
                        title="Editar"
                        onClick={(evento) => {
                          evento.stopPropagation();
                          abrirEdicao(t);
                        }}
                      >
                        <Icon nome="edit" tamanho={16} />
                      </button>
                      <button
                        type="button"
                        className={`${styles.acaoBotao} ${styles.acaoExcluir}`}
                        title="Excluir"
                        onClick={(evento) => {
                          evento.stopPropagation();
                          setIdsExcluir([idTexto]);
                        }}
                      >
                        <Icon nome="delete" tamanho={16} />
                      </button>
                    </div>
                    <span className={`${styles.valor} ${t.tipo === 'R' ? styles.valorReceita : ''}`}>
                      {t.tipo === 'R' ? '+' : ''}
                      {valorParaTexto(t.valor, true)}
                    </span>
                  </div>
                );
              })}
            </section>
          );
        })}

        {restantes > 0 && (
          <div className={styles.mostrarMais}>
            <Button variante="contorno" icone="expand_more" onClick={() => setLimite((atual) => atual + LOTE)}>
              Mostrar mais ({restantes} restante{restantes > 1 ? 's' : ''})
            </Button>
          </div>
        )}
      </div>

      {selecionados.length > 0 && (
        <div className={styles.barraSelecao}>
          <span className={styles.barraTexto}>
            {selecionados.length} selecionada{selecionados.length > 1 ? 's' : ''}
          </span>
          <Button variante="suave" tamanho="sm" icone="category" onClick={() => setCampoLote('categoria')}>
            Categorizar
          </Button>
          <Button variante="suave" tamanho="sm" icone="account_balance" onClick={() => setCampoLote('conta')}>
            Conta
          </Button>
          <Button variante="suave" tamanho="sm" icone="payments" onClick={() => setCampoLote('pagamento')}>
            Pagamento
          </Button>
          <Button variante="perigo" tamanho="sm" icone="delete" onClick={() => setIdsExcluir([...selecionados])}>
            Excluir
          </Button>
          <Button variante="fantasma" tamanho="sm" onClick={() => setSelecionados([])}>
            Cancelar
          </Button>
        </div>
      )}

      <Dialog
        aberto={idsExcluir.length > 0}
        onFechar={() => setIdsExcluir([])}
        titulo={idsExcluir.length > 1 ? `Excluir ${idsExcluir.length} movimentações?` : 'Excluir movimentação?'}
        descricao="Essa ação não pode ser desfeita."
        acoes={
          <>
            <Button variante="fantasma" onClick={() => setIdsExcluir([])}>
              Cancelar
            </Button>
            <Button variante="perigo" icone="delete" carregando={loading} onClick={confirmarExclusao}>
              Excluir
            </Button>
          </>
        }
      />

      <Sheet
        aberto={campoLote !== null}
        onFechar={fecharLote}
        titulo={campoLote ? TITULOS_LOTE[campoLote] : ''}
        subtitulo={`Aplicar em ${selecionados.length} movimentaç${selecionados.length === 1 ? 'ão' : 'ões'}`}
        largura={380}
        rodape={
          <>
            <Button variante="fantasma" onClick={fecharLote}>
              Cancelar
            </Button>
            <Button variante="primaria" icone="check" disabled={!valorLote} carregando={loading} onClick={aplicarLote}>
              Aplicar
            </Button>
          </>
        }
      >
        <SelectMenu
          label={campoLote ? TITULOS_LOTE[campoLote] : ''}
          valor={valorLote}
          onChange={setValorLote}
          opcoes={opcoesLote}
          placeholder="Selecione..."
          busca
        />
      </Sheet>

      <NovaTransacaoSheet
        aberto={sheetAberta}
        onFechar={() => {
          setSheetAberta(false);
          setTransacaoEdicao(null);
        }}
        transacao={transacaoEdicao}
        tipoInicial={tipoInicial}
      />
    </AppShell>
  );
}
